/**
 * Migration Dashboard Component (Simplified & Fixed)
 *
 * A clean, working version of the migration dashboard that handles
 * AI enhancement of beverage data with proper error handling.
 */

import React, { useState, useEffect, useCallback } from "react";
import { enhancedDataMigrationService } from "../services/enhancedDataMigrationService";
import { useRestaurant } from "../contexts/RestaurantContext";
import "./MigrationDashboard.css";

const MigrationDashboard = () => {
  const { currentRestaurant } = useRestaurant();
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [migrationResults, setMigrationResults] = useState(null);
  const [migrationOptions, setMigrationOptions] = useState({
    enableAI: true,
    force: false,
    batchSize: 10,
  });

  // Load migration status
  const loadMigrationStatus = useCallback(async () => {
    if (!currentRestaurant?.id) return;

    try {
      setIsLoading(true);
      const status = await enhancedDataMigrationService.getMigrationStatus(
        currentRestaurant.id
      );
      setMigrationStatus(status);
    } catch (error) {
      console.error("Failed to load migration status:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentRestaurant?.id]);

  useEffect(() => {
    loadMigrationStatus();
  }, [loadMigrationStatus]);

  // Start migration
  const startMigration = async () => {
    if (!currentRestaurant?.id) {
      alert("No restaurant selected");
      return;
    }

    try {
      setIsLoading(true);
      setProgress({ current: 0, total: 0 });
      setMigrationResults(null);

      const results =
        await enhancedDataMigrationService.migrateRestaurantBeverages(
          currentRestaurant.id,
          {
            ...migrationOptions,
            onProgress: (current, total) => {
              setProgress({ current, total });
            },
          }
        );

      setMigrationResults(results);
      await loadMigrationStatus(); // Refresh status

      alert(`Migration completed! Enhanced ${results.success} beverages.`);
    } catch (error) {
      console.error("Migration failed:", error);
      alert(`Migration failed: ${error.message}`);
    } finally {
      setIsLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  // Render migration status
  const renderMigrationStatus = () => {
    if (!migrationStatus) {
      return (
        <div className="migration-status">
          <h3>📊 Loading Status...</h3>
          <p>Checking your beverage data...</p>
        </div>
      );
    }

    const {
      totalBeverages = 0,
      enhancedBeverages = 0,
      needsMigration = 0,
      percentComplete = 0,
    } = migrationStatus;

    return (
      <div className="migration-status">
        <h3>📊 Current Status</h3>

        <div className="status-grid">
          <div className="status-item">
            <div className="status-number">{totalBeverages}</div>
            <div className="status-label">Total Beverages</div>
          </div>
          <div className="status-item">
            <div className="status-number">{needsMigration}</div>
            <div className="status-label">Need Enhancement</div>
          </div>
          <div className="status-item">
            <div className="status-number">{enhancedBeverages}</div>
            <div className="status-label">AI Enhanced</div>
          </div>
        </div>

        <div className="progress-bars">
          <div className="progress-bar">
            <div className="progress-label">
              AI Enhancement Progress: {percentComplete.toFixed(1)}%
            </div>
            <div className="progress-track">
              <div
                className="progress-fill enhancement"
                style={{ width: `${percentComplete}%` }}
              />
            </div>
          </div>
        </div>

        <div className="status-badges">
          {needsMigration === 0 && totalBeverages > 0 && (
            <span className="badge success">✅ All Beverages Enhanced</span>
          )}
          {needsMigration > 0 && (
            <span className="badge pending">
              📝 {needsMigration} Beverages Need Enhancement
            </span>
          )}
          {totalBeverages === 0 && (
            <span className="badge info">
              ℹ️ No beverages found - add some menu items first
            </span>
          )}
        </div>
      </div>
    );
  };

  // Render progress during migration
  const renderProgress = () => {
    if (!isLoading || progress.total === 0) return null;

    const percentage = (progress.current / progress.total) * 100;

    return (
      <div className="live-progress">
        <h4>🚀 AI Enhancement in Progress...</h4>
        <div className="progress-bar">
          <div className="progress-label">
            Processing {progress.current} of {progress.total} beverages (
            {percentage.toFixed(1)}%)
          </div>
          <div className="progress-track">
            <div
              className="progress-fill active"
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
        <p className="progress-note">
          🤖 Each beverage is being analyzed by AI for professional tasting
          notes and enhancements...
        </p>
      </div>
    );
  };

  // Render migration options
  const renderMigrationOptions = () => (
    <div className="migration-options">
      <h3>⚙️ Enhancement Options</h3>

      <div className="option-group">
        <label className="option-label">
          <input
            type="checkbox"
            checked={migrationOptions.enableAI}
            onChange={(e) =>
              setMigrationOptions((prev) => ({
                ...prev,
                enableAI: e.target.checked,
              }))
            }
          />
          <span>🤖 Enable AI Enhancement (Recommended)</span>
        </label>
        <p className="option-description">
          Generate professional tasting notes, food pairings, and quality
          assessments using Anthropic Claude.
        </p>
      </div>

      <div className="option-group">
        <label className="option-label">
          <span>📦 Batch Size:</span>
          <input
            type="number"
            min="1"
            max="50"
            value={migrationOptions.batchSize}
            onChange={(e) =>
              setMigrationOptions((prev) => ({
                ...prev,
                batchSize: parseInt(e.target.value) || 10,
              }))
            }
          />
        </label>
        <p className="option-description">
          Number of beverages to process at once. Start with 5-10 for testing.
        </p>
      </div>

      <div className="option-group">
        <label className="option-label">
          <input
            type="checkbox"
            checked={migrationOptions.force}
            onChange={(e) =>
              setMigrationOptions((prev) => ({
                ...prev,
                force: e.target.checked,
              }))
            }
          />
          <span>🔄 Force Re-enhancement</span>
        </label>
        <p className="option-description">
          Re-enhance beverages that have already been processed.
        </p>
      </div>
    </div>
  );

  // Render results
  const renderResults = () => {
    if (!migrationResults) return null;

    return (
      <div className="migration-results">
        <h3>📊 Migration Results</h3>
        <div className="results-grid">
          <div className="result-item success">
            <strong>{migrationResults.success}</strong>
            <span>Successfully Enhanced</span>
          </div>
          <div className="result-item failed">
            <strong>{migrationResults.failed}</strong>
            <span>Failed</span>
          </div>
          <div className="result-item skipped">
            <strong>{migrationResults.skipped}</strong>
            <span>Skipped</span>
          </div>
        </div>

        {migrationResults.errors && migrationResults.errors.length > 0 && (
          <div className="error-details">
            <h4>❌ Errors:</h4>
            <ul>
              {migrationResults.errors.slice(0, 5).map((error, index) => (
                <li key={`error-${Date.now()}-${index}`}>{error}</li>
              ))}
            </ul>
            {migrationResults.errors.length > 5 && (
              <p>... and {migrationResults.errors.length - 5} more errors</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="migration-dashboard">
      <div className="dashboard-header">
        <h2>🚀 AI Enhancement Dashboard</h2>
        <p>
          Transform your beverage menu with professional AI-powered
          intelligence.
        </p>
      </div>

      {renderMigrationStatus()}
      {renderProgress()}
      {renderMigrationOptions()}

      <div className="action-section">
        <h3>🚀 Enhancement Actions</h3>
        <div className="action-buttons">
          <button
            className="btn-primary"
            onClick={startMigration}
            disabled={isLoading || !migrationOptions.enableAI}
          >
            {isLoading ? "🔄 Enhancing..." : "🚀 Start AI Enhancement"}
          </button>

          <button
            className="btn-outline"
            onClick={loadMigrationStatus}
            disabled={isLoading}
          >
            🔍 Refresh Status
          </button>
        </div>

        <div className="action-notes">
          <p>
            <strong>💡 Pro Tips:</strong>
          </p>
          <ul>
            <li>
              Start with a small batch (5-10 beverages) to test the process
            </li>
            <li>Each beverage costs ~$0.01-0.03 in AI processing</li>
            <li>The process respects API rate limits automatically</li>
            <li>You can safely stop and resume the process anytime</li>
          </ul>
        </div>
      </div>

      {renderResults()}
    </div>
  );
};

export default MigrationDashboard;
